import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db, auth } from "../../lib/firebase";
import { AppUser } from "../../types/user";
import { useAuthContext } from "../../contexts/AuthContext";
import AuthModal from "../../components/AuthModal";
import { isWhiteLabelEnabled } from "../../utils/whiteLabelUtils";
import ClassicTheme from "../../components/themes/ClassicTheme";
import ModernTheme from "../../components/themes/ModernTheme";
import MinimalTheme from "../../components/themes/MinimalTheme";
import { BookOpen, GraduationCap, Clock, Infinity, Star, Phone, Mail, LogOut, User, BarChart3, Receipt, Calendar, CheckCircle2, ChevronLeft, ChevronRight, ChevronDown, Settings, Menu, X } from "lucide-react";

interface Course {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  price: number;
  category?: string;
  level?: string;
}

// Reviews Section Component
function ReviewsCarousel({ themeColor, headingColor }: { themeColor: string; headingColor: string }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const reviews = [
    { name: "Somchai", role: "เรียนจบแล้ว", rating: 5, comment: "คอร์สดีมาก เข้าใจง่าย แนะนำเลยครับ!" },
    { name: "@sarahwong", role: "เรียนไป 2 เดือน", rating: 5, comment: "ได้ความรู้เยอะมากจริงๆ ครูสอนดี อธิบายละเอียด ตัวอย่างชัดเจน เอาไปใช้ได้จริง คุ้มค่ากับเงินที่จ่ายไปมากค่ะ ประทับใจ" },
    { name: "Thanakorn", role: "เรียนจบแล้ว", rating: 5, comment: "สอนละเอียด เข้าใจง่าย มีตัวอย่างประกอบ" },
    { name: "@emmydesign", role: "เรียนไป 3 สัปดาห์", rating: 5, comment: "ครูสอนเข้าใจง่าย ตอบคำถามดี" },
    { name: "Wipada", role: "เรียนจบแล้ว", rating: 5, comment: "เนื้อหาครบถ้วนมาก อัพเดทเป็นปัจจุบัน สอนทุกขั้นตอนละเอียด ตั้งแต่พื้นฐานจนถึงขั้นสูง เหมาะกับทุกระดับเลยค่ะ" },
    { name: "@lisatech", role: "เรียนไป 1 เดือน", rating: 5, comment: "เรียนแล้วนำไปใช้ได้จริง มีตัวอย่างเยอะดี" },
    { name: "Pongsakorn", role: "เรียนจบแล้ว", rating: 5, comment: "คอร์สดีมาก ได้ความรู้เพิ่มเยอะ" },
    { name: "@amycreate", role: "เรียนไป 2 สัปดาห์", rating: 5, comment: "สอนดีมาก เข้าใจง่าย มีแบบฝึกหัดให้ทำด้วย ครูตอบคำถามเร็วมาก ช่วยเหลือดีค่ะ" },
    { name: "Nattapong", role: "เรียนจบแล้ว", rating: 5, comment: "คุ้มค่ามาก ครูสอนเก่งจริงๆ" },
    { name: "@kateblog", role: "เรียนไป 1 เดือน", rating: 5, comment: "ประทับใจมาก สอนละเอียด เข้าใจง่าย" }
  ];

  // Auto slide every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [reviews.length]);

  // Show 3 cards at a time on desktop, 1 on mobile
  const getVisibleReviews = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      visible.push(reviews[(currentIndex + i) % reviews.length]);
    }
    return visible;
  };

  return (
    <div className="py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 font-sans flex items-center justify-center gap-3" style={{ color: headingColor }}>
            <Star className="w-10 h-10" style={{ color: headingColor }} />
            รีวิวจากนักเรียน
          </h2>
          <p className="text-gray-600 text-lg font-sans">
            ความคิดเห็นจากผู้ที่เคยเรียนกับเรา
          </p>
        </div>

        {/* Reviews Carousel */}
        <div className="relative">
          <div className="flex justify-center gap-6 px-4">
            {getVisibleReviews().map((review, index) => (
              <div 
                key={`${currentIndex}-${index}`}
                className="w-72 bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col gap-6 animate-fadeIn border-2 border-gray-100"
                style={{ minHeight: '320px' }}
              >
                {/* Stars */}
                <div className="flex items-center justify-center gap-1">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-current drop-shadow-sm" style={{ color: themeColor }} />
                  ))}
                </div>

                {/* Comment with quote icon */}
                <div className="flex-1 flex items-center">
                  <p className="text-gray-700 text-center font-sans leading-relaxed text-base relative">
                    <span className="text-4xl absolute -left-2 -top-2 opacity-20" style={{ color: themeColor }}>"</span>
                    {review.comment}
                    <span className="text-4xl absolute -right-2 -bottom-4 opacity-20" style={{ color: themeColor }}>"</span>
                  </p>
                </div>

                {/* User info with avatar */}
                <div className="flex items-center gap-3 pt-4 border-t-2 border-gray-100">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md"
                    style={{ background: `linear-gradient(135deg, ${themeColor}, ${getDarkerColor(themeColor, 1)})` }}
                  >
                    {review.name[0]}
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-gray-800 font-sans text-base">{review.name}</h4>
                    <p className="text-sm font-sans" style={{ color: themeColor }}>{review.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex 
                    ? 'w-8 h-3' 
                    : 'w-3 h-3 hover:opacity-70'
                }`}
                style={{
                  backgroundColor: index === currentIndex ? themeColor : '#D1D5DB'
                }}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}

// Helper function to make color darker
const getDarkerColor = (hex: string, steps: number = 1): string => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Darken by reducing each component
  const factor = 1 - (steps * 0.15); // 15% darker per step
  const newR = Math.max(0, Math.floor(r * factor));
  const newG = Math.max(0, Math.floor(g * factor));
  const newB = Math.max(0, Math.floor(b * factor));
  
  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

// Helper function to make color lighter
const getLighterColor = (hex: string, amount: number = 0.3): string => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Lighten by moving towards white (255)
  const newR = Math.min(255, Math.floor(r + (255 - r) * amount));
  const newG = Math.min(255, Math.floor(g + (255 - g) * amount));
  const newB = Math.min(255, Math.floor(b + (255 - b) * amount));
  
  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

// Helper function to get analogous color (shift hue by degrees)
const getAnalogousColor = (hex: string, hueDegrees: number): string => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // Convert RGB to HSL
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  // Shift hue by degrees
  h = (h + hueDegrees / 360) % 1;
  if (h < 0) h += 1;
  
  // Increase saturation and lighten for better visibility
  s = Math.min(1, s * 1.3);
  l = Math.min(0.65, Math.max(0.45, l * 1.2));
  
  // Convert HSL back to RGB
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  let newR, newG, newB;
  if (s === 0) {
    newR = newG = newB = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    newR = hue2rgb(p, q, h + 1/3);
    newG = hue2rgb(p, q, h);
    newB = hue2rgb(p, q, h - 1/3);
  }
  
  // Convert to hex
  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

interface InstructorPublicProfileProps {
  username?: string;
  isSubdomainView?: boolean;
}

export default function InstructorPublicProfile({ username: usernameProp, isSubdomainView = false }: InstructorPublicProfileProps = {}) {
  const { instructorId, username: usernameParam } = useParams<{ instructorId?: string; username?: string }>();
  const username = usernameProp || usernameParam;
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [instructor, setInstructor] = useState<AppUser | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'all' | 'my' | 'history'>('all');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [profileImageError, setProfileImageError] = useState(false);
  const [coverImageError, setCoverImageError] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isWhiteLabel, setIsWhiteLabel] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Read section from URL query parameter
  useEffect(() => {
    const section = searchParams.get('section');
    if (section === 'my' || section === 'history') {
      setActiveSection(section);
    } else {
      setActiveSection('all');
    }
  }, [searchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserDropdown]);

  useEffect(() => {
    async function loadInstructorProfile() {
      try {
        let instructorData: AppUser | null = null;
        let finalInstructorId: string | null = null;

        // Load instructor by ID (FREE tier) or username (PRO tier)
        if (instructorId) {
          // FREE tier: /instructor/:instructorId
          const instructorDoc = await getDoc(doc(db, "users", instructorId));
          if (instructorDoc.exists()) {
            instructorData = { uid: instructorDoc.id, ...instructorDoc.data() } as AppUser;
            finalInstructorId = instructorDoc.id;
          }
        } else if (username) {
          // PRO tier: /:username
          console.log('[InstructorPublicProfile] Querying for username:', username);
          const usersQuery = query(
            collection(db, "users"),
            where("username", "==", username)
          );
          const usersSnap = await getDocs(usersQuery);
          console.log('[InstructorPublicProfile] Query results:', usersSnap.size, 'documents found');
          if (!usersSnap.empty) {
            const userDoc = usersSnap.docs[0];
            const userData = userDoc.data();
            // Accept instructor, admin, or moderator roles
            if (userData.role === "instructor" || userData.role === "admin" || userData.role === "moderator") {
              instructorData = { uid: userDoc.id, ...userData } as AppUser;
              finalInstructorId = userDoc.id;
              console.log('[InstructorPublicProfile] Found instructor:', instructorData.name, instructorData.username);
            } else {
              console.error('[InstructorPublicProfile] User found but role is not instructor/admin/moderator:', userData.role);
            }
          } else {
            console.error('[InstructorPublicProfile] No user found with username:', username);
          }
        }

        if (!instructorData || !finalInstructorId) {
          console.error('[InstructorPublicProfile] Failed to load instructor. instructorData:', instructorData, 'finalInstructorId:', finalInstructorId);
          setLoading(false);
          return;
        }

        setInstructor(instructorData);
        console.log('Instructor data loaded:', {
          name: instructorData.name,
          profileImage: instructorData.profileImage,
          coverImage: instructorData.coverImage,
          username: instructorData.username
        });

        // Update document title with instructor's brand name (only for Pro Yearly and Pro+ Yearly)
        const tier = instructorData.subscriptionTier || '';
        const isYearlyPlan = tier === 'pro_yearly' || tier === 'pro_plus_yearly';
        if (isYearlyPlan) {
          const instructorName = instructorData.displayName || instructorData.businessName || 'EduMi LMS';
          document.title = instructorName;
        } else {
          document.title = 'EduMi LMS';
        }

        // Check if white-label is enabled (Pro+ Yearly only)
        const whiteLabelStatus = await isWhiteLabelEnabled(finalInstructorId);
        setIsWhiteLabel(whiteLabelStatus);

        // Load instructor's courses
        const coursesQuery = query(
          collection(db, "courses"),
          where("instructorId", "==", finalInstructorId)
        );
        const coursesSnap = await getDocs(coursesQuery);
        const coursesData = coursesSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Course[];

        setCourses(coursesData);

        // Load payment history if user is logged in
        if (user) {
          const paymentsQuery = query(
            collection(db, "payments"),
            where("userId", "==", user.uid),
            where("instructorId", "==", finalInstructorId),
            where("status", "==", "completed")
          );
          const paymentsSnap = await getDocs(paymentsQuery);
          const paymentsData = paymentsSnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
          }));
          // Sort by date descending
          paymentsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          setPaymentHistory(paymentsData);
        }
      } catch (error) {
        console.error("Error loading instructor profile:", error);
      } finally {
        setLoading(false);
      }
    }

    loadInstructorProfile();
  }, [instructorId, username, user]);

  const themeColor = instructor?.themeColor || "#8B5CF6";
  const headingColor = instructor?.headingColor || "#1F2937";
  const buttonColor = instructor?.buttonColor || "#8B5CF6";
  const themeLayout = instructor?.themeLayout || "classic";
  
  console.log('[InstructorPublicProfile] Theme settings:', {
    themeLayout,
    themeColor,
    headingColor,
    buttonColor,
    instructorThemeLayout: instructor?.themeLayout
  });
  
  // Filter only active courses (not expired)
  const myCourses = courses.filter(course => {
    if (!user?.purchasedCourses?.includes(course.id)) return false;
    
    // Check if course has expiration date
    const enrollment = user?.enrollments?.find((e: any) => e.courseId === course.id);
    if (!enrollment) return true; // If no enrollment data, show it
    
    // If has expiration date, check if not expired
    if (enrollment.expiresAt) {
      const expirationDate = enrollment.expiresAt.toDate ? enrollment.expiresAt.toDate() : new Date(enrollment.expiresAt);
      return expirationDate > new Date();
    }
    
    return true; // No expiration date means lifetime access
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}05 100%)` }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 mx-auto mb-4" style={{ borderColor: themeColor }}></div>
          <p className="text-gray-600 text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!instructor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-3xl shadow-2xl p-16 text-center max-w-md">
          <div className="text-8xl mb-6 animate-bounce">😕</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-3">
            ไม่พบผู้สอนท่านนี้
          </h2>
          <p className="text-gray-500 text-lg">
            ลิงก์อาจไม่ถูกต้องหรือผู้สอนท่านนี้ไม่มีอยู่ในระบบ
          </p>
        </div>
      </div>
    );
  }

  // Render content with appropriate theme wrapper
  const renderWithTheme = (content: React.ReactNode) => {
    const themeProps = { themeColor, headingColor, buttonColor };
    
    switch (themeLayout) {
      case "modern":
        return <ModernTheme {...themeProps}>{content}</ModernTheme>;
      case "minimal":
        return <MinimalTheme {...themeProps}>{content}</MinimalTheme>;
      case "classic":
      default:
        return <ClassicTheme {...themeProps}>{content}</ClassicTheme>;
    }
  };

  const mainContent = (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-pink-400/10 to-blue-400/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-br from-purple-400/5 to-pink-400/5 rounded-full blur-2xl"></div>
      
      {/* Navigation Menu */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-lg relative">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href={window.location.pathname} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              {instructor.profileImage && !profileImageError ? (
                <img 
                  src={instructor.profileImage} 
                  alt={instructor.name} 
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover ring-2 ring-white shadow-lg"
                  onError={(e) => {
                    console.error('Profile image failed to load:', instructor.profileImage);
                    setProfileImageError(true);
                  }}
                  onLoad={() => console.log('Profile image loaded successfully')}
                />
              ) : (
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white font-bold text-lg md:text-xl shadow-lg" style={{ background: themeColor }}>
                  {instructor.name?.[0]?.toUpperCase() || "T"}
                </div>
              )}
              <div>
                <h2 className="font-bold text-gray-800 font-sans text-sm md:text-base">{instructor.name || "ผู้สอน"}</h2>
                <p className="text-xs text-gray-500 font-sans flex items-center gap-1">
                  <GraduationCap className="w-3 h-3" />
                  ผู้สอนมืออาชีพ
                </p>
              </div>
            </a>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={() => {
                  setActiveSection('all');
                  const coursesSection = document.getElementById('courses-section');
                  if (coursesSection) {
                    coursesSection.scrollIntoView({ behavior: 'smooth', block: '' });
                  }
                }}
                className={`px-4 py-2 rounded-xl font-semibold font-sans transition-all flex items-center gap-2 ${
                  activeSection === 'all'
                    ? 'text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                style={activeSection === 'all' ? { background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` } : {}}
              >
                <BookOpen className="w-4 h-4" />
                คอร์สทั้งหมด
              </button>
              {user && (
                <>
                  <button
                    onClick={() => setActiveSection('my')}
                    className={`px-4 py-2 rounded-xl font-semibold font-sans transition-all flex items-center gap-2 ${
                      activeSection === 'my'
                        ? 'text-white shadow-lg transform scale-105'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    style={activeSection === 'my' ? { background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` } : {}}
                  >
                    <User className="w-4 h-4" />
                    คอร์สของฉัน
                  </button>
                  <button
                    onClick={() => setActiveSection('history')}
                    className={`px-4 py-2 rounded-xl font-semibold font-sans transition-all flex items-center gap-2 ${
                      activeSection === 'history'
                        ? 'text-white shadow-lg transform scale-105'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    style={activeSection === 'history' ? { background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` } : {}}
                  >
                    <Mail className="w-4 h-4" />
                    ประวัติการชำระเงิน
                  </button>
                </>
              )}
              {user ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm" style={{ background: themeColor }}>
                      {user.email?.[0].toUpperCase()}
                    </div>
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  </button>

                  {showUserDropdown && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm text-gray-800 font-semibold">{user.name || user.email}</p>
                        <p className="text-xs text-gray-500 mt-1">{user.email}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {user.role === "instructor" ? "ผู้สอน" : "ผู้เรียน"}
                        </p>
                      </div>

                      {user.role === "student" && (
                        <>
                          <Link
                            to="/profile"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                            onClick={() => setShowUserDropdown(false)}
                          >
                            <User className="w-4 h-4" />
                            โปรไฟล์
                          </Link>
                          <Link
                            to="/profile"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                            onClick={() => setShowUserDropdown(false)}
                          >
                            <Settings className="w-4 h-4" />
                            แก้ไขโปรไฟล์
                          </Link>
                        </>
                      )}

                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <button
                          onClick={() => {
                            auth.signOut();
                            setShowUserDropdown(false);
                          }}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          ออกจากระบบ
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-6 py-2 text-white rounded-xl font-semibold font-sans shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center gap-2"
                  style={{ background: buttonColor }}
                >
                  <User className="w-4 h-4" />
                  เข้าสู่ระบบ
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {showMobileMenu ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {showMobileMenu && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-100 pt-4 space-y-2 animate-slideUp">
              <button
                onClick={() => {
                  setActiveSection('all');
                  setShowMobileMenu(false);
                  const coursesSection = document.getElementById('courses-section');
                  if (coursesSection) {
                    coursesSection.scrollIntoView({ behavior: 'smooth', block: '' });
                  }
                }}
                className={`w-full px-4 py-3 rounded-xl font-semibold font-sans transition-all flex items-center gap-3 ${
                  activeSection === 'all'
                    ? 'text-white shadow-lg'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                style={activeSection === 'all' ? { background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` } : {}}
              >
                <BookOpen className="w-5 h-5" />
                คอร์สทั้งหมด
              </button>
              {user && (
                <>
                  <button
                    onClick={() => {
                      setActiveSection('my');
                      setShowMobileMenu(false);
                    }}
                    className={`w-full px-4 py-3 rounded-xl font-semibold font-sans transition-all flex items-center gap-3 ${
                      activeSection === 'my'
                        ? 'text-white shadow-lg'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    style={activeSection === 'my' ? { background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` } : {}}
                  >
                    <User className="w-5 h-5" />
                    คอร์สของฉัน
                  </button>
                  <button
                    onClick={() => {
                      setActiveSection('history');
                      setShowMobileMenu(false);
                    }}
                    className={`w-full px-4 py-3 rounded-xl font-semibold font-sans transition-all flex items-center gap-3 ${
                      activeSection === 'history'
                        ? 'text-white shadow-lg'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    style={activeSection === 'history' ? { background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` } : {}}
                  >
                    <Mail className="w-5 h-5" />
                    ประวัติการชำระเงิน
                  </button>
                  {user.role === "student" && (
                    <>
                      <Link
                        to="/profile"
                        onClick={() => setShowMobileMenu(false)}
                        className="w-full px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-xl font-semibold font-sans transition-all flex items-center gap-3"
                      >
                        <User className="w-5 h-5" />
                        โปรไฟล์
                      </Link>
                      <Link
                        to="/profile"
                        onClick={() => setShowMobileMenu(false)}
                        className="w-full px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-xl font-semibold font-sans transition-all flex items-center gap-3"
                      >
                        <Settings className="w-5 h-5" />
                        แก้ไขโปรไฟล์
                      </Link>
                    </>
                  )}
                  <div className="border-t border-gray-100 pt-2 mt-2">
                    <button
                      onClick={() => {
                        auth.signOut();
                        setShowMobileMenu(false);
                      }}
                      className="w-full px-4 py-3 text-red-500 hover:bg-red-50 rounded-xl font-semibold font-sans transition-all flex items-center gap-3"
                    >
                      <LogOut className="w-5 h-5" />
                      ออกจากระบบ
                    </button>
                  </div>
                </>
              )}
              {!user && (
                <button
                  onClick={() => {
                    setShowLoginModal(true);
                    setShowMobileMenu(false);
                  }}
                  className="w-full px-4 py-3 text-white rounded-xl font-semibold font-sans shadow-lg flex items-center justify-center gap-3"
                  style={{ background: buttonColor }}
                >
                  <User className="w-5 h-5" />
                  เข้าสู่ระบบ
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>

      {/* Cover Image */}
      {instructor.coverImage && !coverImageError && (
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: "16 / 5" }}>
          <img
            src={instructor.coverImage}
            alt="Cover"
            className="w-full h-full object-cover"
            onError={(e) => {
              console.error('Cover image failed to load:', instructor.coverImage);
              setCoverImageError(true);
            }}
            onLoad={() => console.log('Cover image loaded successfully')}
          />
        </div>
      )}

      {/* Intro Section with Modern Design */}
      {activeSection === 'all' && instructor.introText && (
        <div className="max-w-6xl mx-auto pt-8 pb-16 px-4 relative z-10">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-3xl blur-xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-md border border-white/50">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                {instructor.introImage && (
                  <div className="order-2 md:order-1">
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all"></div>
                      <img
                        src={instructor.introImage}
                        alt="Intro"
                        className="relative w-full rounded-3xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>
                )}
                <div className={instructor.introImage ? "order-1 md:order-2" : "col-span-2 text-center"}>
                  <h2 className="text-4xl md:text-5xl font-bold mb-6 font-sans flex items-center gap-3" style={{ color: headingColor }}>
                    <div className="p-3 rounded-xl shadow-lg" style={{ background: `linear-gradient(135deg, ${themeColor}20, ${themeColor}10)` }}>
                      <GraduationCap className="w-10 h-10" style={{ color: themeColor }} />
                    </div>
                    Welcome ♥
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line font-sans">
                    {instructor.introText}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Section with Modern Cards */}
      {activeSection === 'all' ? (
        <div className="py-16 relative z-10">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
                <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-sm hover:shadow-md transition-all border border-white/50 text-center">
                  <div className="inline-flex p-3 rounded-xl mb-3 shadow-sm" style={{ background: `linear-gradient(135deg, ${themeColor}20, ${themeColor}10)` }}>
                    <BookOpen className="w-10 h-10" style={{ color: themeColor }} />
                  </div>
                  <div className="text-4xl font-bold mb-2 font-sans" style={{ color: themeColor }}>
                    {courses.length}+
                  </div>
                <div className="text-gray-600 font-semibold font-sans">คอร์สทั้งหมด</div>
                </div>
              </div>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
                <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-sm hover:shadow-md transition-all border border-white/50 text-center">
                  <div className="inline-flex p-3 rounded-xl mb-3 bg-gradient-to-br from-green-500/20 to-emerald-500/10 shadow-sm">
                    <GraduationCap className="w-10 h-10 text-green-500" />
                  </div>
                  <div className="text-4xl font-bold mb-2 font-sans text-green-500">
                    100%
                  </div>
                  <div className="text-gray-600 font-semibold font-sans">ผลลัพธ์การเรียน</div>
                </div>
              </div>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
                <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-sm hover:shadow-md transition-all border border-white/50 text-center">
                  <div className="inline-flex p-3 rounded-xl mb-3 bg-gradient-to-br from-orange-500/20 to-amber-500/10 shadow-sm">
                    <Clock className="w-10 h-10 text-orange-500" />
                  </div>
                  <div className="text-4xl font-bold mb-2 font-sans text-orange-500">
                    24/7
                  </div>
                  <div className="text-gray-600 font-semibold font-sans">เรียนได้ทุกเวลา</div>
                </div>
              </div>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
                <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-sm hover:shadow-md transition-all border border-white/50 text-center">
                  <div className="inline-flex p-3 rounded-xl mb-3 shadow-sm" style={{ background: `linear-gradient(135deg, ${themeColor}20, ${themeColor}10)` }}>
                    <Infinity className="w-10 h-10" style={{ color: themeColor }} />
                  </div>
                  <div className="text-4xl font-bold mb-2 font-sans" style={{ color: themeColor }}>
                    ∞
                  </div>
                  <div className="text-gray-600 font-semibold font-sans">เข้าถึงตลอดชีพ</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Courses Section */}
      <div id="courses-section" className="max-w-6xl mx-auto py-16 px-4">
        <div className="mb-12 text-center">
          <h2 className="text-4xl font-bold mb-4 font-sans flex items-center justify-center gap-3" style={{ color: headingColor }}>
            <BookOpen className="w-10 h-10" style={{ color: headingColor }} />
            {activeSection === 'my' ? 'คอร์สของฉัน' : activeSection === 'history' ? 'ประวัติการชำระเงิน' : 'คอร์สเรียนทั้งหมด'}
          </h2>
          <p className="text-gray-600 text-lg font-sans">
            {activeSection === 'my' ? 'คอร์สที่คุณลงทะเบียนแล้ว' : activeSection === 'history' ? 'รายการชำระเงินของคุณ' : 'เลือกคอร์สที่คุณสนใจและเริ่มเรียนรู้ไปกับเรา'}
          </p>
          {activeSection === 'my' && user && (
            <div className="mt-6">
              <button
                onClick={() => navigate(`/progress?instructorId=${instructor.uid}`)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold font-sans text-white shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` }}
              >
                <BarChart3 className="w-5 h-5" />
                ดู Dashboard ความคืบหน้า
              </button>
            </div>
          )}
        </div>

        {activeSection === 'history' && (
          <div className="bg-white rounded-3xl shadow-lg p-8">
            {!user ? (
              <div className="text-center py-16">
                <div className="flex items-center justify-center mb-4">
                  <Receipt className="w-16 h-16" style={{ color: themeColor }} />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3 font-sans">
                  กรุณาเข้าสู่ระบบ
                </h3>
                <p className="text-gray-500 font-sans mb-6">
                  เข้าสู่ระบบเพื่อดูประวัติการชำระเงินของคุณ
                </p>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
                  style={{ background: buttonColor }}
                >
                  เข้าสู่ระบบ
                </button>
              </div>
            ) : paymentHistory.length === 0 ? (
              <div className="text-center py-16">
                <div className="flex items-center justify-center mb-4">
                  <Receipt className="w-16 h-16 text-gray-300" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3 font-sans">
                  ยังไม่มีประวัติการชำระเงิน
                </h3>
                <p className="text-gray-500 font-sans">
                  คุณยังไม่เคยซื้อคอร์สจากผู้สอนท่านนี้
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Receipt className="w-8 h-8" style={{ color: themeColor }} />
                  <h3 className="text-2xl font-bold text-gray-800 font-sans">
                    ประวัติการชำระเงิน
                  </h3>
                </div>
                <div className="space-y-4">
                  {paymentHistory.map((payment) => {
                    const course = courses.find(c => c.id === payment.courseId);
                    return (
                      <div key={payment.id} className="border-2 border-gray-100 rounded-2xl p-6 hover:border-blue-200 transition-all">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4 flex-1">
                            {course?.thumbnail && (
                              <img 
                                src={course.thumbnail} 
                                alt={course.title} 
                                className="w-20 h-20 rounded-xl object-cover" 
                              />
                            )}
                            <div className="flex-1">
                              <h4 className="font-bold text-lg text-gray-800 font-sans mb-2">
                                {course?.title || 'คอร์สเรียน'}
                              </h4>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  <span>{payment.createdAt.toLocaleDateString('th-TH', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}</span>
                                </div>
                                {payment.paymentMethod && (
                                  <span className="px-3 py-1 bg-gray-100 rounded-full text-xs font-semibold">
                                    {payment.paymentMethod === 'promptpay' ? 'พร้อมเพย์' : payment.paymentMethod}
                                  </span>
                                )}
                              </div>
                              {payment.transactionId && (
                                <p className="text-xs text-gray-400 mt-2 font-mono">
                                  รหัสธุรกรรม: {payment.transactionId}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold font-sans" style={{ color: getDarkerColor(themeColor, 1) }}>
                              ฿{(payment.amount || 0).toLocaleString()}
                            </div>
                            <span className="inline-block mt-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                              ชำระเงินแล้ว
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        {activeSection !== 'history' && ((activeSection === 'my' ? myCourses : courses).length === 0 ? (
          <div className="bg-white rounded-3xl shadow-lg p-16 text-center">
            <div className="flex items-center justify-center mb-6">
              <BookOpen className="w-24 h-24 animate-bounce" style={{ color: themeColor }} />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3 font-sans">
              {activeSection === 'my' ? 'ยังไม่มีคอร์สที่ลงทะเบียน' : 'ยังไม่มีคอร์สเรียน'}
            </h3>
            <p className="text-gray-500 font-sans">
              {activeSection === 'my' ? 'เริ่มต้นเรียนรู้ด้วยการซื้อคอร์สแรกของคุณ' : 'ผู้สอนท่านนี้กำลังเตรียมคอร์สเรียนอยู่ กรุณากลับมาดูใหม่ภายหลัง'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(activeSection === 'my' ? myCourses : courses).map((course, index) => {
              const isPurchased = user?.purchasedCourses?.includes(course.id);
              const linkTo = activeSection === 'my' || isPurchased 
                ? `/courses/${course.id}/learn/1?instructorId=${instructor.uid}` 
                : `/courses/${course.id}`;
              
              return (
              <Link 
                key={course.id} 
                to={linkTo}
              >
                <div 
                  className="bg-white rounded-3xl shadow-lg overflow-hidden group cursor-pointer transform hover:scale-105 transition-all duration-300 hover:shadow-2xl"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="w-full h-56 object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    {user?.purchasedCourses?.includes(course.id) && (
                      <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1.5 rounded-full text-xs font-bold font-sans shadow-lg flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4" />
                        ซื้อแล้ว
                      </div>
                    )}
                    {course.level && (
                      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full text-xs font-bold font-sans shadow-lg">
                        {course.level}
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    {course.category && (
                      <div className="inline-block text-white px-3 py-1 rounded-full text-xs font-bold font-sans shadow-md mb-3" style={{ background: themeColor }}>
                        {course.category}
                      </div>
                    )}
                    <h3 className="font-bold text-xl text-gray-800 mb-3 line-clamp-2 font-sans">
                      {course.title}
                    </h3>

                    <p className="text-sm text-gray-600 mb-6 line-clamp-2 font-sans">
                      {course.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="text-3xl font-bold font-sans" style={{ color: buttonColor }}>
                        {course.price === 0 ? (
                          <span className="text-green-500">ฟรี</span>
                        ) : (
                          `฿${course.price.toLocaleString()}`
                        )}
                      </div>
                      <button 
                        onClick={(e) => {
                          if (!user) {
                            e.preventDefault();
                            setShowLoginModal(true);
                          }
                        }}
                        className="text-white px-6 py-3 rounded-xl font-semibold font-sans shadow-lg hover:shadow-xl transition-all transform group-hover:translate-x-1" 
                        style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` }}
                      >
                        {activeSection === 'my' ? 'เรียนต่อ →' : 'ดูรายละเอียด →'}
                      </button>
                    </div>
                  </div>
                </div>
              </Link>
            );
            })}
          </div>
        ))}
      </div>

      {/* Reviews Section */}
      {activeSection === 'all' ? (
        <ReviewsCarousel themeColor={themeColor} headingColor={headingColor} />
      ) : null}

      {/* Bio Section */}
      {activeSection === 'all' && instructor.bio && (
        <div className="max-w-6xl mx-auto py-16 px-4">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-8 h-8" style={{ color: themeColor }} />
              <h2 className="text-3xl md:text-4xl font-bold font-sans" style={{ color: headingColor }}>
                เกี่ยวกับผู้สอน
              </h2>
            </div>
            <style>{`
              .bio-content h1 { font-size: 2em; font-weight: bold; margin: 0.67em 0; }
              .bio-content h2 { font-size: 1.5em; font-weight: bold; margin: 0.75em 0; }
              .bio-content h3 { font-size: 1.17em; font-weight: bold; margin: 0.83em 0; }
              .bio-content h4 { font-size: 1em; font-weight: bold; margin: 1em 0; }
              .bio-content h5 { font-size: 0.83em; font-weight: bold; margin: 1.17em 0; }
              .bio-content h6 { font-size: 0.67em; font-weight: bold; margin: 1.33em 0; }
              .bio-content p { margin: 1em 0; }
              .bio-content strong { font-weight: 700 !important; }
              .bio-content b { font-weight: 700 !important; }
              .bio-content em { font-style: italic !important; }
              .bio-content i { font-style: italic !important; }
              .bio-content u { text-decoration: underline !important; }
              .bio-content s { text-decoration: line-through !important; }
              .bio-content a { color: #3B82F6; text-decoration: underline; }
              .bio-content a:hover { color: #2563EB; }
              .bio-content ul { list-style-type: disc; margin: 1em 0; padding-left: 2em; }
              .bio-content ol { list-style-type: decimal; margin: 1em 0; padding-left: 2em; }
              .bio-content li { margin: 0.5em 0; }
              .bio-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; display: block; cursor: default !important; }
              .bio-content .ql-align-left img,
              .bio-content img.ql-align-left,
              .bio-content p:not([class*="ql-align"]) img { margin: 8px 0 8px 0 !important; display: block !important; }
              .bio-content .ql-align-center img,
              .bio-content img.ql-align-center { margin: 8px auto !important; display: block !important; }
              .bio-content .ql-align-right img,
              .bio-content img.ql-align-right { margin: 8px 0 8px auto !important; display: block !important; }
              .bio-content video { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; }
              .bio-content iframe { max-width: 100%; border-radius: 8px; margin: 8px 0; }
              .bio-content .ql-align-center { text-align: center; }
              .bio-content .ql-align-right { text-align: right; }
              .bio-content .ql-align-left { text-align: left; }
              .bio-content .ql-size-small { font-size: 0.75em; }
              .bio-content .ql-size-large { font-size: 1.5em; }
              .bio-content .ql-size-huge { font-size: 2.5em; }
              .bio-content blockquote { border-left: 4px solid #e5e7eb; padding-left: 1em; margin: 1em 0; color: #6b7280; }
              .bio-content pre { background: #f3f4f6; padding: 1em; border-radius: 8px; overflow-x: auto; }
              .bio-content code { background: #f3f4f6; padding: 0.2em 0.4em; border-radius: 4px; font-family: monospace; }
            `}</style>
            <div 
              className="bio-content leading-relaxed font-sans"
              dangerouslySetInnerHTML={{ __html: instructor.bio }}
            />
          </div>
        </div>
      )}


      {/* Footer */}
      <div className="text-white py-12" style={{ background: `linear-gradient(135deg, ${themeColor} 0%, ${getDarkerColor(themeColor, 2)} 100%)` }}>
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="mb-6">
            <h3 className="text-2xl font-bold mb-2 font-sans">{instructor.name || "ผู้สอน"}</h3>
            <p className="text-white/80 font-sans">ช่องทางการติดต่อและติดตามผลงาน</p>
          </div>

          {/* Social Links */}
          {(instructor.socialLinks?.facebook || instructor.socialLinks?.line || instructor.socialLinks?.instagram || instructor.socialLinks?.youtube || instructor.socialLinks?.website) && (
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {instructor.socialLinks?.facebook && (
                <a href={instructor.socialLinks.facebook.startsWith('http') ? instructor.socialLinks.facebook : `https://facebook.com/${instructor.socialLinks.facebook}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-all transform hover:scale-105">
                  <div className="w-10 h-10 bg-[#1877F2] rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </div>
                  <span className="font-medium text-white font-sans">Facebook</span>
                </a>
              )}
              {instructor.socialLinks?.line && (
                <a href={instructor.socialLinks.line.startsWith('http') ? instructor.socialLinks.line : `https://line.me/ti/p/@${instructor.socialLinks.line}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-all transform hover:scale-105">
                  <div className="w-10 h-10 bg-[#00B900] rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                    </svg>
                  </div>
                  <span className="font-medium text-white font-sans">Line</span>
                </a>
              )}
              {instructor.socialLinks?.instagram && (
                <a href={instructor.socialLinks.instagram.startsWith('http') ? instructor.socialLinks.instagram : `https://instagram.com/${instructor.socialLinks.instagram}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-all transform hover:scale-105">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)'}}>
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <span className="font-medium text-white font-sans">Instagram</span>
                </a>
              )}
              {instructor.socialLinks?.youtube && (
                <a href={instructor.socialLinks.youtube.startsWith('http') ? instructor.socialLinks.youtube : `https://youtube.com/@${instructor.socialLinks.youtube}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-all transform hover:scale-105">
                  <div className="w-10 h-10 bg-[#FF0000] rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </div>
                  <span className="font-medium text-white font-sans">YouTube</span>
                </a>
              )}
              {instructor.socialLinks?.website && (
                <a href={instructor.socialLinks.website.startsWith('http') ? instructor.socialLinks.website : `https://${instructor.socialLinks.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-all transform hover:scale-105">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: themeColor }}>
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm9.5 12c0 1.49-.346 2.9-.959 4.153l-3.675-10.06A9.456 9.456 0 0 1 21.5 12zM12 21.5c-1.778 0-3.442-.49-4.868-1.343l5.178-15.04 5.305 14.53c.035.087.078.167.125.244A9.454 9.454 0 0 1 12 21.5zM2.5 12c0-1.293.259-2.525.728-3.648l4.014 11.001A9.456 9.456 0 0 1 2.5 12zm7.868-6.486L6.5 16.5l-.002-.005-3.867-10.59A9.456 9.456 0 0 1 12 2.5c1.171 0 2.292.213 3.327.602-.053.084-.103.173-.147.267l-4.812 13.145z"/>
                    </svg>
                  </div>
                  <span className="font-medium text-white font-sans">Website</span>
                </a>
              )}
            </div>
          )}

          {!isWhiteLabel && (
            <div className="border-t border-gray-700 pt-6">
              <p className="text-gray-400 text-sm font-sans">
                Powered by <a href="https://coursedee.web.app" target="_blank" rel="noopener noreferrer" className="font-bold hover:underline" style={{ color: themeColor }}>EduMi</a> - แพลตฟอร์มสร้างคอร์สออนไลน์
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        themeColor={themeColor}
      />
    </div>
  );

  return renderWithTheme(mainContent);
}
